import { cashComparison, type CashWorkflowSnapshotV1 } from '@pokerroom/types';
import { useTranslation } from 'react-i18next';

/** Live comparison stays outside historical totals and print. */
export function CashComparison({ snapshot }: { snapshot: CashWorkflowSnapshotV1 }) {
  const { t } = useTranslation('accounting');
  const rows = cashComparison(snapshot).filter((b) => b.kind !== 'EVENT_BASKET');
  const settlement = snapshot.dailySettlement;
  return (
    <section className="space-y-2 print:hidden" aria-label={t('comparison.title')}>
      <h3 className="font-semibold">{t('comparison.title')}</h3>
      <p>{t('comparison.basis')}</p>
      <p>{snapshot.asOf}</p>
      <details className="break-all text-xs">
        <summary>{t('overview.showTechnical')}</summary>
        {snapshot.sessionId} · {snapshot.version}
      </details>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm text-right [&_td]:p-2 [&_th]:p-2">
          <thead>
            <tr>
              {['bag', 'booked', 'pending', 'projected'].map((key) => (
                <th key={key}>{t(`comparison.${key}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id}>
                <th>
                  {t(b.kind === 'ZIPA_DRAWER' ? 'overview.drawer' : `cashWorkflow.bags.${b.kind}`)}
                </th>
                <td>{b.amount}</td>
                <td>{b.pending ?? t('overview.unavailable')}</td>
                <td>{b.projected ?? t('overview.unavailable')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>{t('comparison.member', { amount: snapshot.memberZipaBalance })}</p>
      {rows
        .filter((b) => b.kind === 'ZIPA_DRAWER')
        .map((b) => (
          <p key={b.id}>
            {t('comparison.differences', {
              booked: b.bookedDifference,
              projected: b.projectedDifference ?? t('overview.unavailable'),
            })}
          </p>
        ))}
      <p role="status">
        {t(settlement?.complete ? 'comparison.complete' : 'comparison.incomplete')}
      </p>
      {(settlement?.blockers ?? snapshot.blockers).map((b, index) => (
        <p key={`${b.id}:${index}`} role="status">
          {t(b.name, { defaultValue: b.name })}
        </p>
      ))}
      <details>
        <summary>{t('comparison.routes')}</summary>
        <ul>
          {(settlement?.routes ?? []).map((r, i) => (
            <li key={`${r.sourceId}:${i}`}>
              {r.sourceType} · {r.fromLocationId} → {r.toLocationId} · {r.amount} TWD
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
